/**
 * One-time migration from localStorage `tezus-route` → canonical URL.
 * Phase 3: never write `tezus-route` during navigation. Phase 4: isolated in
 * its own module so `paths.js` (the public URL-mapping API) no longer exposes
 * the legacy `{ page, store, tenantContext, tab, ... }` onNav-shaped target.
 *
 * Why the one-time read still exists: a browser that hasn't opened the Super
 * Admin since Phase 3 may still carry the `tezus-route` key. There is no
 * adoption telemetry and no reliable signal that every active session has
 * already consumed it, so removing the read now would silently drop the
 * "resume where I left off" redirect for those sessions (they would fall back
 * to `/dashboard` instead of their saved page). Do not invent a removal date.
 * To remove later: delete this module, `src/navigation/legacy-route-state.js`,
 * and their tests; replace `RootRedirect` with a direct
 * `<Navigate to="/dashboard" replace />`.
 */

import {
  TEZUS_ROUTE_STORAGE_KEY,
  buildPersistedRoute,
  loadSavedRoute,
} from '../navigation/legacy-route-state.js';
import { buildTabPath, MODULE_TAB_CONFIG } from './module-tabs.js';
import { serializeSearchParams, searchParamsToString } from './search-params.js';
import {
  BILLING_SEARCH_SCHEMA,
  REPAIRS_SEARCH_SCHEMA,
  INTEGRATIONS_SEARCH_SCHEMA,
  OBSERVABILIDADE_SEARCH_SCHEMA,
  AUDITORIA_SEARCH_SCHEMA,
  PLANOS_PERMISSOES_SEARCH_SCHEMA,
  KB_SEARCH_SCHEMA,
} from './search-param-schemas.js';
import { PAGE_TO_PATH, resolveTenantId } from './paths.js';

function extractTenantIdFromContext(target) {
  return resolveTenantId(target.store)
    || resolveTenantId(target.storeId)
    || resolveTenantId(target.tenant_id)
    || resolveTenantId(target.tenantContext)
    || (target.tenantContext?.tenant_id != null ? String(target.tenantContext.tenant_id) : null)
    || (target.tenantContext?.id != null ? String(target.tenantContext.id) : null);
}

function extractTicketId(target) {
  if (target.openTicketId != null && String(target.openTicketId).trim() !== '') {
    return String(target.openTicketId).trim();
  }
  if (target.contextTicket?.id != null) return String(target.contextTicket.id);
  if (target.ticketId != null) return String(target.ticketId);
  if (target.articleContext?.ticketId != null) return String(target.articleContext.ticketId);
  if (target.articleContext?.ticket?.id != null) return String(target.articleContext.ticket.id);
  return null;
}

/**
 * Convert a legacy onNav-shaped target (`{ page, store, tenantContext, tab, ... }`)
 * to a canonical location. Only used by the migration below — no production
 * component builds a target object like this anymore (use `paths.*` instead).
 * @returns {{ pathname: string, search?: string, page: string, tenantId?: string|null, state?: object } | { error: string }}
 */
export function legacyRouteToUrl(target = {}) {
  if (!target || typeof target !== 'object') {
    return { error: 'invalid_target' };
  }
  const page = target.page;
  if (!page || typeof page !== 'string') {
    return { error: 'missing_page' };
  }

  const optionalState = {};
  if (target.store != null) optionalState.store = target.store;
  if (target.tenantContext != null) optionalState.tenantContext = target.tenantContext;
  if (target.contextTicket != null) optionalState.contextTicket = target.contextTicket;
  if (target.articleContext != null) optionalState.articleContext = target.articleContext;

  if (page === 'loja' || page === 'cockpit') {
    const tenantId = extractTenantIdFromContext(target);
    if (!tenantId) {
      return { error: 'missing_tenant' };
    }
    let pathname;
    if (page === 'cockpit') {
      const tabId = target.tab || target.activeTab || 'overview';
      pathname = buildTabPath('cockpit', tabId, { tenantId: String(tenantId) });
    } else {
      pathname = `/lojas/${encodeURIComponent(tenantId)}`;
    }
    return {
      pathname,
      page,
      tenantId: String(tenantId),
      state: Object.keys(optionalState).length ? optionalState : undefined,
    };
  }

  // Tickets: prefer path when openTicketId present (structure behind MVP guard).
  if (page === 'tickets') {
    const ticketId = extractTicketId(target);
    const tabId = target.tab || target.activeTab;
    let pathname = PAGE_TO_PATH.tickets;
    if (ticketId) {
      // Deep link by id is structural only — no by-id API yet.
      // Keep openTicketId in transit; do not invent /:ticketId as delivered deep link.
      // Still navigate to inbox root so guard/page can consume openTicketId.
      pathname = PAGE_TO_PATH.tickets;
      if (tabId) pathname = buildTabPath('tickets', tabId) || pathname;
    } else if (tabId) {
      pathname = buildTabPath('tickets', tabId) || pathname;
    }
    return {
      pathname,
      page,
      state: Object.keys(optionalState).length ? optionalState : undefined,
    };
  }

  // KB new article from ticket context
  if (page === 'knowledge-base') {
    const ticketId = extractTicketId(target);
    const wantsNew = target.subPage === 'new'
      || target.articleContext?.mode === 'new'
      || target.articleContext?.type === 'new'
      || target.createArticle === true;
    if (wantsNew || (ticketId && target.articleContext)) {
      const pathname = '/suporte/base-conhecimento/artigos/novo';
      const search = ticketId
        ? searchParamsToString(serializeSearchParams({ ticketId }, KB_SEARCH_SCHEMA))
        : '';
      return {
        pathname,
        search,
        page,
        state: Object.keys(optionalState).length ? optionalState : undefined,
      };
    }
    const tabId = target.tab || target.activeTab;
    const pathname = tabId
      ? (buildTabPath('knowledge-base', tabId) || PAGE_TO_PATH[page])
      : PAGE_TO_PATH[page];
    return {
      pathname,
      page,
      state: Object.keys(optionalState).length ? optionalState : undefined,
    };
  }

  // Modules with shareable tenantContext → ?tenantId=
  const tenantId = extractTenantIdFromContext(target);
  const ticketId = extractTicketId(target);

  if (page === 'billing') {
    const tabId = target.tab || target.activeTab || 'overview';
    // Legacy billing inadimplência tabs → dedicated module
    if (tabId === 'inadimplencia' || tabId === 'regua') {
      const search = tenantId
        ? searchParamsToString(serializeSearchParams({ tenantId }, BILLING_SEARCH_SCHEMA))
        : '';
      return { pathname: '/inadimplencia', search, page: 'inadimplencia', tenantId };
    }
    const pathname = buildTabPath('billing', tabId) || '/billing';
    const search = tenantId
      ? searchParamsToString(serializeSearchParams({ tenantId }, BILLING_SEARCH_SCHEMA))
      : '';
    return {
      pathname,
      search,
      page,
      tenantId: tenantId || undefined,
      state: Object.keys(optionalState).length ? optionalState : undefined,
    };
  }

  if (page === 'repairs') {
    const tabId = target.tab || target.activeTab || 'diagnostico';
    const pathname = buildTabPath('repairs', tabId) || '/repairs';
    const search = searchParamsToString(serializeSearchParams({
      tenantId: tenantId || null,
      ticketId: ticketId || null,
    }, REPAIRS_SEARCH_SCHEMA));
    return {
      pathname,
      search,
      page,
      tenantId: tenantId || undefined,
      state: Object.keys(optionalState).length ? optionalState : undefined,
    };
  }

  if (page === 'integrations' || page === 'observabilidade' || page === 'auditoria' || page === 'planos-permissoes' || page === 'inadimplencia') {
    const moduleKey = page === 'integrations'
      ? 'integrations'
      : page === 'observabilidade'
        ? 'observabilidade'
        : page === 'inadimplencia'
          ? 'inadimplencia'
          : null;
    const tabId = target.tab || target.activeTab;
    let pathname = PAGE_TO_PATH[page];
    if (moduleKey && tabId) {
      pathname = buildTabPath(moduleKey, tabId) || pathname;
    }
    const schema = page === 'integrations'
      ? INTEGRATIONS_SEARCH_SCHEMA
      : page === 'observabilidade'
        ? OBSERVABILIDADE_SEARCH_SCHEMA
        : page === 'auditoria'
          ? AUDITORIA_SEARCH_SCHEMA
          : page === 'planos-permissoes'
            ? PLANOS_PERMISSOES_SEARCH_SCHEMA
            : null;
    const search = (schema && tenantId)
      ? searchParamsToString(serializeSearchParams({ tenantId }, schema))
      : '';
    return {
      pathname,
      search,
      page,
      tenantId: tenantId || undefined,
      state: Object.keys(optionalState).length ? optionalState : undefined,
    };
  }

  // Generic tab on modules that support MODULE_TAB_CONFIG keyed by page
  const moduleKeyForPage = Object.keys(MODULE_TAB_CONFIG).find(
    (k) => MODULE_TAB_CONFIG[k].page === page,
  );
  if (moduleKeyForPage && (target.tab || target.activeTab)) {
    const pathname = buildTabPath(moduleKeyForPage, target.tab || target.activeTab) || PAGE_TO_PATH[page];
    return {
      pathname,
      page,
      state: Object.keys(optionalState).length ? optionalState : undefined,
    };
  }

  const pathname = PAGE_TO_PATH[page];
  if (!pathname) {
    return { error: 'unknown_page' };
  }
  return {
    pathname,
    page,
    state: Object.keys(optionalState).length ? optionalState : undefined,
  };
}

/** Canonical pathname for a legacy page id (+ tenant, for `loja`/`cockpit`). */
export function getCanonicalPathForPage(page, tenantId) {
  if (page === 'loja' && tenantId) return `/lojas/${encodeURIComponent(tenantId)}`;
  if (page === 'cockpit' && tenantId) return `/lojas/${encodeURIComponent(tenantId)}/cockpit`;
  return PAGE_TO_PATH[page] || null;
}

/**
 * Convert a saved legacy route object to a canonical pathname for `/` redirect.
 * Returns `/dashboard` when the saved route is missing or invalid.
 */
export function savedRouteToPathname(saved, deps = {}) {
  const getItem = typeof deps.getItem === 'function'
    ? deps.getItem
    : () => (saved != null ? JSON.stringify(saved) : null);

  const loaded = typeof deps.loadSavedRoute === 'function'
    ? deps.loadSavedRoute({ ...deps, getItem })
    : loadSavedRoute({ ...deps, getItem });

  if (!loaded || !loaded.page) return '/dashboard';

  if (loaded.page === 'loja' || loaded.page === 'cockpit') {
    const tenantId = loaded.store?.id
      || loaded.store?.tenant_id
      || loaded.storeId;
    const path = getCanonicalPathForPage(loaded.page, tenantId);
    return path || '/lojas';
  }

  const mapped = legacyRouteToUrl({ page: loaded.page });
  if (mapped.error) return '/dashboard';
  return mapped.pathname;
}

/**
 * Build the tezus-route payload shape (kept only for unit tests of the old format).
 * @deprecated Phase 3 — do not write to storage.
 */
export function buildPersistedFromUrl({ page, store, storeId } = {}) {
  return buildPersistedRoute({
    page,
    store: store || (storeId ? { id: storeId } : undefined),
  });
}

/**
 * Read tezus-route once, convert to pathname, and remove the key.
 * Safe to call on every `/` visit — subsequent calls see no key and return /dashboard.
 * Never writes a new key: only `getItem` (read) and `removeItem` (delete) are used.
 *
 * @returns {{ pathname: string, migrated: boolean, hadKey: boolean }}
 */
export function consumeSavedRouteOnce(deps = {}) {
  const storageKey = deps.storageKey || TEZUS_ROUTE_STORAGE_KEY;
  const getItem = typeof deps.getItem === 'function'
    ? deps.getItem
    : (key) => (typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null);
  const removeItem = typeof deps.removeItem === 'function'
    ? deps.removeItem
    : (key) => {
      if (typeof localStorage !== 'undefined') localStorage.removeItem(key);
    };

  let raw = null;
  try {
    raw = getItem(storageKey);
  } catch (e) {
    return { pathname: '/dashboard', migrated: false, hadKey: false };
  }

  if (raw == null || raw === '') {
    return { pathname: '/dashboard', migrated: false, hadKey: false };
  }

  const pathname = savedRouteToPathname(null, {
    ...deps,
    storageKey,
    getItem: () => raw,
    removeItem,
  });

  try {
    removeItem(storageKey);
  } catch (e) { /* ignore */ }

  return {
    pathname: pathname || '/dashboard',
    migrated: true,
    hadKey: true,
  };
}

/**
 * Drop a leftover tezus-route key without navigating.
 * Used when the user already has an explicit URL (not `/`). Only removes.
 */
export function discardSavedRouteKey(deps = {}) {
  const storageKey = deps.storageKey || TEZUS_ROUTE_STORAGE_KEY;
  const removeItem = typeof deps.removeItem === 'function'
    ? deps.removeItem
    : (key) => {
      if (typeof localStorage !== 'undefined') localStorage.removeItem(key);
    };
  const getItem = typeof deps.getItem === 'function'
    ? deps.getItem
    : (key) => (typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null);

  let hadKey = false;
  try {
    const raw = getItem(storageKey);
    hadKey = raw != null && raw !== '';
    if (hadKey) removeItem(storageKey);
  } catch (e) { /* ignore */ }
  return { discarded: hadKey };
}

// Minimal, explicit public surface (aliases for readability at call sites).
export const consumeLegacySavedRouteOnce = consumeSavedRouteOnce;
export const discardLegacySavedRoute = discardSavedRouteKey;

export { TEZUS_ROUTE_STORAGE_KEY, buildPersistedRoute, loadSavedRoute };
